// Battle-map ATMOSPHERE — makes a level feel like a section of the target planet's surface, themed by the
// world you're deploying to (passed from the campaign as zone/type/temp + the real star color). It owns a
// gradient sky dome with the system's star as the sun, retints the scene's fog + lights + exposure, and
// drives temperature-forward VFX: rising embers + heat glow on HOT worlds, fast side-blown snow on COLD
// ones, drifting dust on temperate/garden. Only active in a campaign op; the standalone sandbox is untouched.

import * as THREE from "three";
import type { Scene } from "../world/scene";
import { SkyBaker, type ScatterParams } from "./scattering";
import { CLOUD_COMMON, cloudParamsForBiome, type CloudParams } from "./clouds";
import { setFogBiome, setFogSky, FOG_BIOMES } from "./fog";
import { BLUE_NOISE_64 } from "./blueNoise64";

// One shared 64×64 void-and-cluster blue-noise tile for the cloud march's screen-space dither. Module-level
// singleton so every battle reuses the same 4KB upload. Nearest + Repeat + no mips are load-bearing: linear
// filtering or mipmaps would average away the blue spectrum that makes the residual grain fine and unstructured.
let _blueNoiseTex: THREE.DataTexture | null = null;
function blueNoiseTex(): THREE.DataTexture {
  if (!_blueNoiseTex) {
    const t = new THREE.DataTexture(BLUE_NOISE_64, 64, 64, THREE.RedFormat, THREE.UnsignedByteType);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.minFilter = t.magFilter = THREE.NearestFilter;
    t.generateMipmaps = false;
    t.needsUpdate = true;
    _blueNoiseTex = t;
  }
  return _blueNoiseTex;
}

export interface BiomeInput {
  zone: "hot" | "temperate" | "cold";
  ptype: string;
  ptemp: number;
  garden: boolean;
  starHex: string; // real star color (no '#'), e.g. "ffb060"
}

interface Preset {
  zenith: number; horizon: number;
  sunSize: number; sunGlow: number; stars: number;
  fogNear: number; fogFar: number;
  sunI: number; ambient: number; ambientI: number;
  hemiSky: number; hemiGround: number; hemiI: number; exposure: number;
  windDir: THREE.Vector3; windSpeed: number; streak: number; windColor: number; windCount: number; windOpacity: number;
  ground: number; ground2: number; relief: number; rough: number; // terrain surface colors + relief amplitude/scale
}

export interface Biome { key: string; preset: Preset; star: THREE.Color; }

export function resolveBiome(input: BiomeInput): Biome {
  const key = input.zone === "temperate" && input.garden ? "garden" : input.zone;
  return { key, preset: PRESETS[key] ?? PRESETS.temperate, star: new THREE.Color("#" + (input.starHex || "ffb060")) };
}

/** The world's horizon sky color as a 6-digit hex (no '#'). Used to tint the orbit→surface "landing"
 *  wash on BOTH the campaign side (fade-out) and the battle side (fade-in), so the page cut is invisible. */
export function biomeHorizonHex(input: BiomeInput): string {
  return (resolveBiome(input).preset.horizon >>> 0).toString(16).padStart(6, "0");
}

// the tech deployment barrier is faction cyan across all worlds (it's equipment, not planet)
export const PERIMETER_COLOR = 0x3fd0e6;

// --- per-biome ATMOSPHERE COMPOSITION (feeds the physically-based scattering bake) ------------------
// Earth's Rayleigh is blue-dominant (5.8,13.5,33.1)e-3. Alien worlds tune the coefficients + air
// thickness + sun elevation so the sky COLOR emerges from physics instead of hand-picked gradients,
// while staying art-directed to the world's mood (scorched=thin reddened, garden=thick blue-green…).
const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);
export function sunDir(elevDeg: number, aziDeg = 37): THREE.Vector3 {
  const e = (elevDeg * Math.PI) / 180, a = (aziDeg * Math.PI) / 180;
  return new THREE.Vector3(Math.cos(e) * Math.sin(a), Math.sin(e), Math.cos(e) * Math.cos(a)).normalize();
}
interface SkySpec { params: ScatterParams; observerH: number; sunDisc: number; }
const SCATTER: Record<string, SkySpec> = {
  // scorched near-airless: thin, dust/CO2-reddened air, low fierce sun → deep red horizon, dark zenith
  hot: { params: { sunDir: sunDir(24), sunI: 7, betaR: V(13e-3, 4.8e-3, 2.4e-3), betaM: V(10e-3, 3.6e-3, 1.6e-3), Hr: 7, Hm: 1.5, planetR: 6371, atmoR: 6437, mieG: 0.80, exposure: 1.08 }, observerH: 1.0, sunDisc: 0.9992 },
  // frozen thin air: weak pale-blue scattering, higher wan sun → washed pale-white sky
  cold: { params: { sunDir: sunDir(42), sunI: 5.5, betaR: V(6.0e-3, 9.5e-3, 15e-3), betaM: V(4e-3, 4.6e-3, 5.2e-3), Hr: 9, Hm: 1.0, planetR: 6371, atmoR: 6429, mieG: 0.68, exposure: 1.08 }, observerH: 1.0, sunDisc: 0.9996 },
  // Mars-rust dead plain: dusty medium air, amber sun → salmon/rust sky
  temperate: { params: { sunDir: sunDir(32), sunI: 6.5, betaR: V(8.6e-3, 7.4e-3, 6.4e-3), betaM: V(7e-3, 4.8e-3, 3.2e-3), Hr: 8, Hm: 1.3, planetR: 6371, atmoR: 6453, mieG: 0.78, exposure: 1.15 }, observerH: 1.0, sunDisc: 0.9994 },
  // garden (thick air): near-Earth but tinted green-blue, high soft sun → hazy cyan-green sky
  garden: { params: { sunDir: sunDir(46), sunI: 7, betaR: V(5.4e-3, 12e-3, 20e-3), betaM: V(4e-3, 4.6e-3, 4.2e-3), Hr: 8, Hm: 1.2, planetR: 6371, atmoR: 6483, mieG: 0.75, exposure: 1.2 }, observerH: 1.0, sunDisc: 0.9995 },
};

const PRESETS: Record<string, Preset> = {
  // scorched, near-airless: fierce orange horizon, dark zenith, harsh big sun, rising embers + heat glow
  hot: {
    zenith: 0x1a0805, horizon: 0xc63c10, sunSize: 0.03, sunGlow: 55, stars: 0.35,
    fogNear: 1200, fogFar: 11000, sunI: 2.5, ambient: 0x3a1c0c, ambientI: 0.55,
    hemiSky: 0xd05018, hemiGround: 0x2a1006, hemiI: 0.75, exposure: 1.55,
    windDir: new THREE.Vector3(0.18, 1.0, 0.12).normalize(), windSpeed: 520, streak: 34, windColor: 0xff7a2e, windCount: 460, windOpacity: 0.5,
    ground: 0x2a1409, ground2: 0x5a2410, relief: 1000, rough: 1.1,
  },
  // frozen ice world: pale blue-white sky, small weak sun, stars, fast side-blown snow
  cold: {
    zenith: 0x0a1826, horizon: 0x9fc0d8, sunSize: 0.012, sunGlow: 420, stars: 0.7,
    fogNear: 1800, fogFar: 13000, sunI: 1.15, ambient: 0x24343f, ambientI: 0.62,
    hemiSky: 0xaecde2, hemiGround: 0x3a4a58, hemiI: 0.95, exposure: 1.12,
    windDir: new THREE.Vector3(1.0, -0.06, 0.32).normalize(), windSpeed: 1650, streak: 150, windColor: 0xe4f0ff, windCount: 720, windOpacity: 0.55,
    ground: 0x8ba0b4, ground2: 0xdfeefc, relief: 620, rough: 0.8,
  },
  // Mars-rust dead plain: dusty salmon sky, amber sun, wind-blown dust
  temperate: {
    zenith: 0x3a2216, horizon: 0xc88a60, sunSize: 0.02, sunGlow: 120, stars: 0.12,
    fogNear: 1800, fogFar: 12500, sunI: 1.9, ambient: 0x3a2a1c, ambientI: 0.55,
    hemiSky: 0xc49a76, hemiGround: 0x4a3624, hemiI: 0.82, exposure: 1.3,
    windDir: new THREE.Vector3(1.0, 0.02, 0.28).normalize(), windSpeed: 820, streak: 62, windColor: 0xcaa06a, windCount: 480, windOpacity: 0.42,
    ground: 0x5a3418, ground2: 0x8a5a30, relief: 760, rough: 0.95,
  },
  // garden world (thicker air): hazy cyan-green sky, soft warm sun, gentle pollen drift
  garden: {
    zenith: 0x16303c, horizon: 0x9fc4b2, sunSize: 0.018, sunGlow: 110, stars: 0.0,
    fogNear: 1500, fogFar: 9500, sunI: 1.7, ambient: 0x2c3a32, ambientI: 0.6,
    hemiSky: 0xa6cbb4, hemiGround: 0x3a4636, hemiI: 0.95, exposure: 1.32,
    windDir: new THREE.Vector3(0.6, 0.05, 0.42).normalize(), windSpeed: 360, streak: 30, windColor: 0xbfe0c8, windCount: 360, windOpacity: 0.4,
    ground: 0x3e4a2a, ground2: 0x6a7a48, relief: 520, rough: 0.85,
  },
};

// The sky dome SAMPLES the baked scattering cubemap (a single texture lookup — the 24×6-step raymarch
// ran once at bake time), overlays animated daytime stars for thin-air worlds, and raymarches a thin
// cloud slab. The dome is a TRUE infinite skybox: the vertex stage pins its depth to the far plane
// (`.xyww`) and the mesh is drawn last, so terrain always wins the depth test — the sky can never be
// approached, escaped, or occlude the battlefield, and the cloud march only touches sky pixels.
const SKY_VERT = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = position; // dome is only ever translated (never rotated) ⇒ this is the world view direction
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_Position = clip.xyww; // z = w ⇒ NDC depth 1.0 (the far plane): a skybox at infinity
  }
`;
const SKY_FRAG = /* glsl */ `
  precision highp float;
  out vec4 gFragColor;          // GLSL3 requires an explicit fragment output (no gl_FragColor)
  uniform mat4 uViewProj;       // camera projection · viewInverse — for projecting the cloud point to depth
  varying vec3 vDir;
  uniform samplerCube uSky;
  uniform vec3 uSunDir;
  uniform float uStars, uTime;
  // clouds
  uniform float uCloudOn, uCloudCoverage, uCloudScale, uCloudY0, uCloudY1, uCloudDensity, uCloudSteps;
  uniform float uPuff, uDither, uEvolve, uSoft; // DEV: billow height, anti-band dither, churn, edge softness
  uniform vec3 uCamPos, uCloudTint, uCloudSun;
  uniform vec2 uCloudWind;
  uniform sampler2D uBlueNoize;
  float hash(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
  ${CLOUD_COMMON}
  void main(){
    vec3 d = normalize(vDir);
    vec3 col = textureCube(uSky, d).rgb; // baked Rayleigh+Mie sky (display-encoded)
    float outDepth = gl_FragCoord.z; // default = the vertex's far-plane depth (rewritten only under a cloud)
    if (uStars > 0.001 && d.y > 0.04) {  // daytime stars for thin-air worlds, faded out near the sun
      float sun = smoothstep(0.9990, 0.99985, dot(d, uSunDir));
      vec2 g = floor((d.xz / (d.y + 0.25)) * 240.0);
      float s = hash(g);
      float star = step(0.994, s) * uStars * (0.6 + 0.4 * sin(uTime * 3.0 + s * 120.0));
      col += vec3(star) * (1.0 - sun);
    }
    // ---- volumetric clouds: short raymarch through a thin high slab (sky pixels only) ----
    // abs(d.y): works whether the eye is below the deck (looking up) or above it (looking down onto it).
    if (uCloudOn > 0.5 && abs(d.y) > 0.02) {
      float t0 = (uCloudY0 - uCamPos.y) / d.y;
      float t1 = (uCloudY1 - uCamPos.y) / d.y;
      float tEnter = max(min(t0, t1), 0.0);
      float tExit = max(t0, t1);
      if (tExit > tEnter) {
        float seg = min(tExit - tEnter, 26000.0); // clamp grazing rays so cost stays bounded
        float steps = uCloudSteps;
        float dt = seg / steps;
        float mu = dot(d, uSunDir);
        float g = 0.42;
        float phase = (1.0 - g * g) / pow(1.0 + g * g - 2.0 * g * mu, 1.5) / 12.566; // Henyey–Greenstein
        float trans = 1.0, lit = 0.0;
        float sft = clamp(uSoft, 0.0, 1.0);   // guard against typed-in out-of-range values
        // ANTI-BAND DITHER — PER-STEP STRATIFIED, so the march can NEVER slide off the deck. (That off-slab
        // slide WAS the "gaps"/salt-and-pepper: offsetting the whole fixed-length march start meant a big offset
        // pushed a short march entirely past the slab, so ~1/3 of pixels sampled nothing → holes.) Instead every
        // step i samples ONCE inside its OWN slice [i,i+1)·dt at a blue-noise sub-step offset — so every pixel
        // always covers the whole column (no holes) with low, stratified variance. Blue tile keyed on
        // gl_FragCoord ⇒ residual grain is fine, unstructured, screen-space-static (no swim/flicker). Softness is
        // carried by the deterministic fades below, so the jitter only needs a ≤1-step slice (amp clamps it there).
        float bn = texture2D(uBlueNoize, gl_FragCoord.xy / 64.0).r;
        float amp = clamp(uDither, 0.0, 1.0);
        float ce = uTime * uEvolve; // shared evolution time (churn); ground shadows pass the same
        for (int i = 0; i < 16; i++) {
          if (float(i) >= steps || trans < 0.02) break;
          float jit = mix(0.5, fract(bn + float(i) * 0.618034), amp); // ∈[0,1): stratified sub-step offset per step
          vec3 P = uCamPos + d * (tEnter + (float(i) + jit) * dt);
          float cov = cloudCover(P.xz, uCloudCoverage, uCloudScale, uCloudWind, ce);
          if (cov > 0.001) {
            // PUFFY 2.5-D: a flat condensation BASE + a cauliflower TOP whose height follows coverage
            // (thick masses billow higher), times a finer height-varying noise for internal lumps — so the
            // deck reads as rounded 3-D volume with soft edges instead of a flat uniform slab.
            float slabH = uCloudY1 - uCloudY0;
            float topY = min(uCloudY1, uCloudY0 + slabH * 0.12 + cov * uPuff * (slabH * 0.88));
            // DECK-THICKNESS-AWARE soft edges = the voluminous "blurred" look, done DETERMINISTICALLY (zero
            // variance ⇒ zero grain). The fades scale with THIS column's height (col0), and uSoft sets how much
            // of the column is soft ramp vs solid plateau (uSoft 0 ⇒ ~42% plateau/crisp, uSoft 1 ⇒ ~8% plateau/
            // very soft). Scaling by col0 (not the slab) means the thin low-coverage decks never collapse.
            float col0 = topY - uCloudY0;
            float fadeTot = mix(0.58, 0.92, sft);             // fraction of the column given to soft edges
            float base = smoothstep(uCloudY0, uCloudY0 + col0 * fadeTot * 0.46, P.y);
            float top = 1.0 - smoothstep(topY - col0 * fadeTot * 0.54, topY, P.y);
            float lump = 0.5 + 0.55 * clFbm((P.xz + P.y * 0.25) / (uCloudScale * 0.4) + uCloudWind / uCloudScale + ce * 0.03);
            // wider fades thin the integrated density ⇒ compensate so opacity holds as softness rises.
            float dens = cov * base * top * lump * uCloudDensity * (1.0 + sft * 0.9);
            if (dens > 0.001) {
              // cheap sun visibility: one coverage hop toward the sun ⇒ Beer self-shadowing
              vec2 sxz = P.xz + uSunDir.xz * (260.0 / max(uSunDir.y, 0.2));
              float covS = cloudCover(sxz, uCloudCoverage, uCloudScale, uCloudWind, ce);
              float sun = exp(-covS * 3.0);   // horizontal Beer self-shadow toward the sun (0 shadow .. 1 lit)
              // VERTICAL FORM SHADE: light falls from above, so a cloud's base sits in self-shadow and its crown
              // catches the sun — dark base → bright crown is the cue that reads as a lit 3-D mass, not a flat
              // glow. The (0.6 + …·sun) keeps thick decks from collapsing to uniform gray; phase = silver lining.
              float hFac = clamp((P.y - uCloudY0) / max(col0, 1.0), 0.0, 1.0);
              // the sunlit CROWN gets a direct top-light regardless of horizontal self-shadow, so aerial
              // (top-down) views read as bright cloud tops rather than a dim gray deck.
              float energy = mix(0.28, 1.15, hFac * hFac) * (0.6 + 0.55 * sun)
                           + phase * sun * 0.5 + smoothstep(0.68, 1.0, hFac) * 0.4;
              float dstep = dens * dt * 0.0006; // optical depth per step (physical path length ⇒ thicker at grazing)
              lit += trans * energy * dstep;
              trans *= exp(-dstep);
            }
          }
        }
        float alpha = clamp(1.0 - trans, 0.0, 1.0) * smoothstep(0.02, 0.16, abs(d.y));
        // tiny floor only (was 0.4, which lit clouds uniformly = glow); the form + direct light now lives in lit.
        vec3 cloudCol = uCloudTint * (0.04 + lit * 1.15) + uCloudSun * (lit * 0.5);
        col = mix(col, cloudCol, alpha);
        // DEPTH: place a visible cloud at its near-slab distance so it can OCCLUDE terrain when the eye is
        // above the deck looking down. From below / at the horizon the cloud distance is GREATER than the
        // terrain in front, so the same depth test keeps it correctly behind the ground. (Sky background,
        // alpha≈0, keeps the far-plane depth set at the top of main.)
        if (alpha > 0.02) {
          vec4 cclip = uViewProj * vec4(uCamPos + d * tEnter, 1.0);
          outDepth = clamp(0.5 + 0.5 * cclip.z / cclip.w, 0.0, 1.0);
        }
      }
    }
    gFragColor = vec4(col, 1.0);
    gl_FragDepth = outDepth; // real per-pixel cloud depth (GLSL3); lets a cloud occlude terrain from above
  }
`;

export class Atmosphere {
  private key: string;
  private preset: Preset;
  private starColor: THREE.Color;
  private sky: SkySpec;
  private baker = new SkyBaker(128);
  private dome: THREE.Mesh;
  private skyMat: THREE.ShaderMaterial;
  private wind: THREE.LineSegments;
  private windPos: Float32Array;
  private windVel: Float32Array; // per-particle velocity
  private streaks: Float32Array; // per-particle streak length
  private dir: THREE.Vector3;
  private hx = 5200; private hy = 3200; // wind box half-extents (x/z) and height
  private time = 0;
  private cloud: CloudParams;

  constructor(biome: Biome, sunOverride?: THREE.Vector3) {
    this.key = biome.key;
    this.preset = biome.preset;
    this.starColor = biome.star;
    this.sky = SCATTER[biome.key] ?? SCATTER.temperate;
    if (sunOverride) {
      // REAL sun position (from the deploy-time orbital geometry) — clone the spec so the shared
      // per-biome SCATTER singleton is never mutated across battles.
      this.sky = { ...this.sky, params: { ...this.sky.params, sunDir: sunOverride.clone().normalize() } };
      // the Preset's lighting constants were art-tuned for the biome's default elevation; scale the
      // sun's punch with elevation and warm it near the horizon so ground lighting follows the sky.
      const elev = Math.asin(THREE.MathUtils.clamp(this.sky.params.sunDir.y, -1, 1));
      const low = 1 - THREE.MathUtils.smoothstep(elev, 0.2, 0.9); // 0 high noon → 1 near horizon
      this.preset = { ...this.preset, sunI: this.preset.sunI * (1 - low * 0.35) };
      this.starColor = biome.star.clone().lerp(new THREE.Color(1.0, 0.6, 0.35), low * 0.45);
    }

    this.dir = this.preset.windDir.clone();
    this.cloud = cloudParamsForBiome(biome.key);
    const sunCol = this.starColor;

    // sky dome — samples the baked scattering cubemap (bake happens in install(), once the renderer exists)
    this.skyMat = new THREE.ShaderMaterial({
      uniforms: {
        uSky: { value: this.baker.texture },
        uSunDir: { value: this.sky.params.sunDir.clone() },
        uStars: { value: this.preset.stars },
        uTime: { value: 0 },
        // clouds (static per battle) + per-frame (uCamPos / uCloudWind)
        uCloudOn: { value: 1 },
        uCloudCoverage: { value: this.cloud.coverage },
        uCloudScale: { value: this.cloud.scale },
        uCloudY0: { value: this.cloud.y0 },
        uCloudY1: { value: this.cloud.y1 },
        uCloudDensity: { value: this.cloud.density },
        uCloudSteps: { value: this.cloud.steps },
        uPuff: { value: 1 },    // billow-height multiplier (DEV)
        uDither: { value: 1 },  // anti-band jitter amplitude 0..1 (DEV) — sub-step only; >1 is clamped (would gap)
        uSoft: { value: 0.5 },  // deterministic edge softness / voluminous blur (DEV) — grain-free
        uEvolve: { value: 1 },  // churn-rate multiplier (DEV); ground shadows get the same value in lockstep
        uBlueNoize: { value: blueNoiseTex() },
        uCloudTint: { value: new THREE.Color(this.cloud.tint[0], this.cloud.tint[1], this.cloud.tint[2]) },
        uCloudSun: { value: new THREE.Color(sunCol.r, sunCol.g, sunCol.b) },
        uCamPos: { value: new THREE.Vector3() },
        uCloudWind: { value: new THREE.Vector2() },
        uViewProj: { value: new THREE.Matrix4() },
      },
      vertexShader: SKY_VERT, fragmentShader: SKY_FRAG,
      side: THREE.BackSide, depthWrite: false, depthTest: true, fog: false,
      glslVersion: THREE.GLSL3, // SKY_FRAG writes gl_FragDepth (a real cloud depth) so clouds occlude from above
    });
    // depth pinned to the far plane in the vertex shader ⇒ draw AFTER opaque terrain (renderOrder) so
    // the depth test discards sky pixels behind geometry; radius only needs to sit inside the far clip.
    this.dome = new THREE.Mesh(new THREE.SphereGeometry(22000, 32, 16), this.skyMat);
    this.dome.renderOrder = 10;
    this.dome.frustumCulled = false;

    // wind streaks
    const N = this.preset.windCount;
    this.windPos = new Float32Array(N * 6);
    this.windVel = new Float32Array(N * 3);
    this.streaks = new Float32Array(N);
    for (let i = 0; i < N; i++) this.seedParticle(i);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.windPos, 3));
    const mat = new THREE.LineBasicMaterial({
      color: this.preset.windColor, transparent: true, opacity: this.preset.windOpacity,
      blending: THREE.AdditiveBlending, depthWrite: false, fog: false,
    });
    this.wind = new THREE.LineSegments(geo, mat);
    this.wind.frustumCulled = false;
  }

  install(scene: Scene): void {
    // aim the shadow sun to match the sky's sun, then BAKE the scattering cubemap with that direction
    // (one-time raymarch; the per-frame sky is just a cubemap lookup). Re-callable if the sun ever moves.
    scene.setSunDir(this.sky.params.sunDir);
    this.baker.bake(scene.renderer, this.sky.params, this.starColor, this.sky.observerH, this.sky.sunDisc);
    this.skyMat.uniforms.uSky.value = this.baker.texture;
    scene.applyBiome({
      fog: this.preset.horizon, fogNear: this.preset.fogNear, fogFar: this.preset.fogFar,
      sunColor: this.starColor.getHex(), sunIntensity: this.preset.sunI,
      ambient: this.preset.ambient, ambientI: this.preset.ambientI,
      hemiSky: this.preset.hemiSky, hemiGround: this.preset.hemiGround, hemiI: this.preset.hemiI,
      exposure: this.preset.exposure,
    });
    // AERIAL FOG: fade opaque geometry toward the ACTUAL baked sky (not a flat hex). setFogSky flips on
    // aerial mode + points the fog at the same cubemap the dome samples; setFogBiome loads this world's
    // density/haze/glow. scene.fog stays a THREE.Fog so USE_FOG compiles + the classic fallback has values.
    setFogBiome(FOG_BIOMES[this.key] ?? FOG_BIOMES.temperate);
    setFogSky(this.baker.texture, this.sky.params.sunDir, this.starColor);
    scene.add(this.dome);
    scene.add(this.wind);
  }

  /** Re-bake the sky for a new sun direction (for a future moving-sun / time-of-day system). */
  setSun(scene: Scene, dir: THREE.Vector3): void {
    this.sky.params.sunDir.copy(dir).normalize();
    this.skyMat.uniforms.uSunDir.value.copy(this.sky.params.sunDir);
    scene.setSunDir(this.sky.params.sunDir);
    this.baker.bake(scene.renderer, this.sky.params, this.starColor, this.sky.observerH, this.sky.sunDisc);
  }

  private seedParticle(i: number): void {
    const p = this.preset;
    // head somewhere in the box (later wrapped to follow the focus in update)
    const hx = (Math.random() * 2 - 1) * this.hx;
    const hz = (Math.random() * 2 - 1) * this.hx;
    const hy = Math.random() * this.hy + (p.windDir.y > 0.5 ? 40 : 120);
    const spd = p.windSpeed * (0.7 + Math.random() * 0.6);
    // velocity = wind dir * speed + a little perpendicular jitter for organic drift
    this.windVel[i * 3] = this.dir.x * spd + (Math.random() - 0.5) * spd * 0.15;
    this.windVel[i * 3 + 1] = this.dir.y * spd + (Math.random() - 0.5) * spd * 0.1;
    this.windVel[i * 3 + 2] = this.dir.z * spd + (Math.random() - 0.5) * spd * 0.15;
    this.streaks[i] = p.streak * (0.6 + Math.random() * 0.8);
    this.writeParticle(i, hx, hy, hz);
  }

  private writeParticle(i: number, hx: number, hy: number, hz: number): void {
    const o = i * 6;
    this.windPos[o] = hx; this.windPos[o + 1] = hy; this.windPos[o + 2] = hz;
    this.windPos[o + 3] = hx - this.dir.x * this.streaks[i];
    this.windPos[o + 4] = hy - this.dir.y * this.streaks[i];
    this.windPos[o + 5] = hz - this.dir.z * this.streaks[i];
  }

  /** The sky's sun direction (world), for whatever needs to agree with it (e.g. terra cloud shadows). */
  getSunDir(): THREE.Vector3 {
    return this.sky.params.sunDir.clone();
  }

  /** The sky-dome mesh (for the per-sector GPU-cost probe). */
  getDome(): THREE.Mesh {
    return this.dome;
  }

  /** Per-frame cloud drift (world-XZ offset in studs). Feed the SAME offset to the terra material so
   *  ground shadows track the clouds overhead. */
  setCloudScroll(x: number, z: number): void {
    (this.skyMat.uniforms.uCloudWind.value as THREE.Vector2).set(x, z);
  }

  /** Live-tune a scalar cloud uniform on the sky dome by name (DEV panel). Shared-with-shadow uniforms
   *  (coverage/scale/evolve) should ALSO be pushed to the terra material so ground shadows track. */
  setCloudUniform(name: string, value: number): void {
    const u = this.skyMat.uniforms[name];
    if (u && typeof u.value === "number") u.value = value;
  }

  /** Feed the camera's current view-projection so the cloud march can write a real per-pixel depth (used
   *  to occlude terrain when the eye is above the deck). Call each frame before rendering. */
  setViewProj(cam: THREE.Camera): void {
    cam.updateMatrixWorld();
    const m = this.skyMat.uniforms.uViewProj.value as THREE.Matrix4;
    m.copy(cam.matrixWorld).invert();     // view = world⁻¹
    m.premultiply(cam.projectionMatrix);  // proj · view (same space terrain depth is written in)
  }

  update(dt: number, camPos: THREE.Vector3, fx: number, fz: number): void {
    this.time += dt;
    this.skyMat.uniforms.uTime.value = this.time;
    // the dome follows the EYE (full 3-D position), so the sky is always exactly `radius` away in every
    // direction: it can't be approached or escaped, and the baked sun/horizon never swim off-center.
    this.dome.position.copy(camPos);
    (this.skyMat.uniforms.uCamPos.value as THREE.Vector3).copy(camPos);
    // wind.position stays at the origin (particles carry absolute world coords) — no per-frame reset needed
    const N = this.streaks.length;
    const halfX = this.hx, spanX = halfX * 2, halfY = this.hy; // loop-invariant wrap extents
    for (let i = 0; i < N; i++) {
      let hx = this.windPos[i * 6] + this.windVel[i * 3] * dt;
      let hy = this.windPos[i * 6 + 1] + this.windVel[i * 3 + 1] * dt;
      let hz = this.windPos[i * 6 + 2] + this.windVel[i * 3 + 2] * dt;
      // wrap into the box centered on the moving focus
      const rx = hx - fx, rz = hz - fz;
      if (rx > halfX) hx -= spanX; else if (rx < -halfX) hx += spanX;
      if (rz > halfX) hz -= spanX; else if (rz < -halfX) hz += spanX;
      if (hy > halfY) hy -= halfY; else if (hy < 20) hy += halfY;
      this.writeParticle(i, hx, hy, hz);
    }
    (this.wind.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
  }
}
